import React from 'react';
import FeatureDescription from '../core/FeatureDescription';
import SectionH2 from '../heading/SectionH2';
import Buzzer from '../../img/buzzer.png';
import LandingPhone from '../app/LandingPhone';
import { PROFESSIONS } from '../../data/professions';

const HowItWorks = ({ channel }) => {
  const getHowItWorksDescriptions = () => {
    if (channel === 'instacart') {
      return [
        {
          title: '1. Tap to unlock your door',
          description: 'Tap to enable automatic entry for Instacart shoppers in the Lowkey app',
          app: 'setup',
        },
        {
          title: '2. Instacart shopper dials your unit',
          description:
            'The Instacart shopper dials your unit from the buzzer, either by dialing your unit code or using the buzzer directory',
          secondaryDescription:
            'If a Passcode is enabled, the Instacart shopper will be prompted to enter the Lowkey passcode after first dialing your unit',
          imageSrc: Buzzer,
          imageAlt: 'Apartment buzzer',
          imageStyle: 'h-80 sm:h-68 lg:h-80 rounded-3xl',
        },
        {
          title: '3. Instacart shopper is buzzed in',
          description:
            "Lowkey buzzes in the Instacart shopper on your behalf and you'll immediately receive a push notification letting you know they've arrived",
          app: 'notification',
        },
      ];
    } else if (channel === 'doordash' || channel === 'ubereats') {
      const serviceName = channel === 'doordash' ? 'DoorDash' : 'UberEats';
      return [
        {
          title: '1. Tap to unlock your door',
          description: `Tap to enable automatic entry for ${serviceName} drivers in the Lowkey app`,
          app: 'setup',
        },
        {
          title: `2. ${serviceName} driver dials your unit`,
          description: `The ${serviceName} driver dials your unit from the buzzer, either by dialing your unit code or using the buzzer directory`,
          secondaryDescription: `If a Passcode is enabled, the ${serviceName} driver will be prompted to enter the Lowkey passcode after first dialing your unit`,
          imageSrc: Buzzer,
          imageAlt: 'Apartment buzzer',
          imageStyle: 'h-80 sm:h-68 lg:h-80 rounded-3xl',
        },
        {
          title: `3. ${serviceName} driver is buzzed in`,
          description: `Lowkey buzzes in the ${serviceName} driver on your behalf and you'll immediately receive a push notification letting you know they've arrived`,
          app: 'notification',
        },
      ];
    } else if (channel === 'caregivers') {
      return [
        {
          title: '1. Tap to unlock your door',
          description: 'Tap to enable secure entry for caregivers in the Lowkey app',
          app: 'setup',
        },
        {
          title: '2. Caregiver dials your unit',
          description:
            'The caregiver dials your unit from the buzzer, either by dialing your unit code or using the buzzer directory',
          secondaryDescription:
            'If a Passcode is enabled, the caregiver will be prompted to enter the Lowkey passcode after first dialing your unit',
          imageSrc: Buzzer,
          imageAlt: 'Apartment buzzer',
          imageStyle: 'h-80 sm:h-68 lg:h-80 rounded-3xl',
        },
        {
          title: '3. Caregiver is buzzed in',
          description:
            "Lowkey buzzes in the caregiver on your behalf and you'll immediately receive a push notification letting you know they've arrived",
          app: 'notification',
        },
      ];
    } else if (channel === 'assisted_living') {
      return [
        {
          title: '1. Tap to unlock the door',
          description: 'Tap to enable secure entry for caregivers and visitors in the Lowkey app',
          app: 'setup',
        },
        {
          title: '2. Caregiver dials the unit',
          description:
            'The caregiver or visitor dials the unit from the buzzer, using either the unit code or the buzzer directory',
          secondaryDescription:
            'If a Passcode is enabled, they will be prompted to enter the Lowkey passcode after first dialing the unit',
          imageSrc: Buzzer,
          imageAlt: 'Apartment buzzer',
          imageStyle: 'h-80 sm:h-68 lg:h-80 rounded-3xl',
        },
        {
          title: '3. Visitor is buzzed in automatically',
          description:
            "Lowkey buzzes them in on your behalf and you'll immediately receive a push notification letting you know they've arrived",
          app: 'notification',
        },
      ];
    } else if (channel === 'buzz_in_clients') {
      return [
        {
          title: '1. Set up automatic entry for clients',
          description: 'Tap to enable secure entry for your clients in the Lowkey app',
          app: 'setup',
        },
        {
          title: '2. Client arrives and dials your unit',
          description:
            'Your client dials your unit from the buzzer, using either your unit code or the buzzer directory',
          secondaryDescription:
            'If enabled, the client will be prompted to enter a unique Lowkey passcode for added security',
          imageSrc: Buzzer,
          imageAlt: 'Apartment buzzer',
          imageStyle: 'h-80 sm:h-68 lg:h-80 rounded-3xl',
        },
        {
          title: '3. Client is buzzed in automatically',
          description:
            "Lowkey buzzes in the client on your behalf and you'll immediately receive a push notification letting you know they've arrived",
          app: 'notification',
        },
      ];
    } else if (channel === 'delivery') {
      return [
        {
          title: '1. Set up automatic entry for deliveries',
          description: 'Tap to enable secure entry for delivery drivers in the Lowkey app',
          app: 'setup',
        },
        {
          title: '2. Delivery driver arrives and dials your unit',
          description:
            'The delivery driver dials your unit from the buzzer, using either your unit code or the buzzer directory',
          secondaryDescription:
            'If enabled, the driver will be prompted to enter a unique Lowkey passcode for added security',
          imageSrc: Buzzer,
          imageAlt: 'Apartment buzzer',
          imageStyle: 'h-80 sm:h-68 lg:h-80 rounded-3xl',
        },
        {
          title: '3. Delivery driver is buzzed in automatically',
          description:
            "Lowkey buzzes in the delivery driver on your behalf and you'll immediately receive a push notification letting you know your package has arrived",
          app: 'notification',
        },
      ];
    } else if (channel === 'rover') {
      return [
        {
          title: '1. Set up automatic entry for your walker',
          description: 'Tap to enable secure entry for your Rover dog walker or sitter in the Lowkey app',
          app: 'setup',
        },
        {
          title: '2. Your walker dials your unit',
          description:
            'Your Rover walker dials your unit from the buzzer, using either your unit code or the buzzer directory',
          secondaryDescription:
            'If a Passcode is enabled, they will be prompted to enter the Lowkey passcode after first dialing your unit',
          imageSrc: Buzzer,
          imageAlt: 'Apartment buzzer',
          imageStyle: 'h-80 sm:h-68 lg:h-80 rounded-3xl',
        },
        {
          title: '3. Your walker is buzzed in automatically',
          description:
            "Lowkey buzzes in your walker on your behalf and you'll immediately receive a push notification letting you know they've arrived",
          app: 'notification',
        },
      ];
    } else if (channel === 'pet_sitting') {
      return [
        {
          title: '1. Set up automatic entry for your sitter',
          description: 'Tap to enable secure entry for your pet sitter in the Lowkey app',
          app: 'setup',
        },
        {
          title: '2. Your sitter dials your unit',
          description:
            'Your pet sitter dials your unit from the buzzer, using either your unit code or the buzzer directory',
          secondaryDescription:
            'If a Passcode is enabled, they will be prompted to enter the Lowkey passcode after first dialing your unit',
          imageSrc: Buzzer,
          imageAlt: 'Apartment buzzer',
          imageStyle: 'h-80 sm:h-68 lg:h-80 rounded-3xl',
        },
        {
          title: '3. Your sitter is buzzed in automatically',
          description:
            "Lowkey buzzes in your sitter on your behalf and you'll immediately receive a push notification letting you know they've arrived",
          app: 'notification',
        },
      ];
    } else if (channel === 'airbnb') {
      return [
        {
          title: "1. Set access for your guest's stay",
          description: 'Schedule buzzer access in the Lowkey app for the dates of each guest booking',
          app: 'setup',
        },
        {
          title: '2. Guest dials your unit',
          description:
            'Your guest dials your unit from the buzzer, using either your unit code or the buzzer directory',
          secondaryDescription:
            'If a Passcode is enabled, they will be prompted to enter the Lowkey passcode after first dialing your unit',
          imageSrc: Buzzer,
          imageAlt: 'Apartment buzzer',
          imageStyle: 'h-80 sm:h-68 lg:h-80 rounded-3xl',
        },
        {
          title: '3. Guest is buzzed in automatically',
          description:
            "Lowkey buzzes in your guest on your behalf and you'll receive a push notification — access expires automatically at checkout",
          app: 'notification',
        },
      ];
    } else if (channel === 'short_term_rentals') {
      return [
        {
          title: '1. Schedule access per booking',
          description: 'Set time-limited buzzer access for each guest, cleaner, or vendor in the Lowkey app',
          app: 'setup',
        },
        {
          title: '2. They dial the unit',
          description:
            'Your guest or vendor dials the unit from the buzzer, using either the unit code or the buzzer directory',
          secondaryDescription:
            'If a Passcode is enabled, they will be prompted to enter the Lowkey passcode after first dialing the unit',
          imageSrc: Buzzer,
          imageAlt: 'Apartment buzzer',
          imageStyle: 'h-80 sm:h-68 lg:h-80 rounded-3xl',
        },
        {
          title: '3. They are buzzed in automatically',
          description:
            'Lowkey buzzes them in on your behalf and logs the entry, so you have a record of access across every unit',
          app: 'notification',
        },
      ];
    } else if (channel === 'amazon_packages' || channel === 'groceries') {
      const who = channel === 'groceries' ? 'grocery delivery driver' : 'delivery driver';
      return [
        {
          title: '1. Set up automatic entry for deliveries',
          description: `Tap to enable secure entry for your ${who} in the Lowkey app`,
          app: 'setup',
        },
        {
          title: '2. Driver arrives and dials your unit',
          description: `The ${who} dials your unit from the buzzer, using either your unit code or the buzzer directory`,
          secondaryDescription:
            'If a Passcode is enabled, they will be prompted to enter the Lowkey passcode after first dialing your unit',
          imageSrc: Buzzer,
          imageAlt: 'Apartment buzzer',
          imageStyle: 'h-80 sm:h-68 lg:h-80 rounded-3xl',
        },
        {
          title: '3. Driver is buzzed in automatically',
          description:
            "Lowkey buzzes in the driver on your behalf and you'll immediately receive a push notification letting you know your delivery has arrived",
          app: 'notification',
        },
      ];
    } else if (channel === 'roommates') {
      return [
        {
          title: '1. Share access with your roommates',
          description: 'Invite your roommates in the Lowkey app so everyone can buzz people in',
          app: 'setup',
        },
        {
          title: '2. A guest dials your unit',
          description:
            'Your guest dials your unit from the buzzer, using either your unit code or the buzzer directory',
          secondaryDescription:
            'If a Passcode is enabled, they will be prompted to enter the Lowkey passcode after first dialing your unit',
          imageSrc: Buzzer,
          imageAlt: 'Apartment buzzer',
          imageStyle: 'h-80 sm:h-68 lg:h-80 rounded-3xl',
        },
        {
          title: '3. Any roommate can buzz them in',
          description:
            "Whoever is available buzzes the guest in from their own phone, and the whole household gets a notification that they've arrived",
          app: 'notification',
        },
      ];
    } else if (channel === 'property_access_software') {
      return [
        {
          title: '1. Add Lowkey to each building',
          description:
            'Give every unit a Lowkey number to program into the existing panel, with no rewiring or new hardware',
          app: 'setup',
        },
        {
          title: '2. A visitor dials the unit',
          description:
            'A guest, delivery, or vendor dials the unit from the buzzer, using either the unit code or the buzzer directory',
          secondaryDescription:
            'If a passcode is enabled, they will be prompted to enter the Lowkey passcode after first dialing the unit',
          imageSrc: Buzzer,
          imageAlt: 'Apartment buzzer',
          imageStyle: 'h-80 sm:h-68 lg:h-80 rounded-3xl',
        },
        {
          title: '3. They are buzzed in automatically',
          description:
            "Lowkey opens the front door on the resident's behalf, and every entry is logged with a push notification",
          app: 'notification',
        },
      ];
    } else if (PROFESSIONS[channel]) {
      const noun = PROFESSIONS[channel].clientNoun;
      const one = noun === 'students' ? 'student' : 'client';
      return [
        {
          title: `1. Set up automatic entry for your ${noun}`,
          description: `Tap to enable secure entry for your ${noun} in the Lowkey app`,
          app: 'setup',
        },
        {
          title: `2. Your ${one} arrives and dials your unit`,
          description: `Your ${one} dials your unit from the buzzer, using either your unit code or the buzzer directory`,
          secondaryDescription: `If a Passcode is enabled, they will be prompted to enter the Lowkey passcode after first dialing your unit`,
          imageSrc: Buzzer,
          imageAlt: 'Apartment buzzer',
          imageStyle: 'h-80 sm:h-68 lg:h-80 rounded-3xl',
        },
        {
          title: `3. Your ${one} is buzzed in automatically`,
          description: `Lowkey buzzes them in on your behalf and you'll get a discreet push notification letting you know they've arrived`,
          app: 'notification',
        },
      ];
    } else {
      return [
        {
          title: '1. Tap to unlock your door',
          description: 'Tap to enable a Timer, Passcode, or Routine in the Lowkey app',
          app: 'setup',
        },
        {
          title: '2. Guest dials your unit',
          description:
            'Have your guest dial your unit from the buzzer, either by dialing your unit code or using the buzzer directory',
          secondaryDescription:
            'If a Passcode is enabled, your guest will be prompted to enter the Lowkey passcode after first dialing your unit',
          imageSrc: Buzzer,
          imageAlt: 'Apartment buzzer',
          imageStyle: 'h-80 sm:h-68 lg:h-80 rounded-3xl',
        },
        {
          title: '3. Guest is buzzed in',
          description:
            "Lowkey will buzz in the guest on your behalf and you'll immediately receive a push notification letting you know they've arrived",
          app: 'notification',
        },
      ];
    }
  };

  const howItWorksDescriptions = getHowItWorksDescriptions();

  return (
    <div className="px-10">
      <div className="flex flex-col items-center pb-0 pt-1 sm:pb-8">
        <SectionH2 title={'How It Works'} />
        <div className="max-w-4xl space-y-3 text-center font-space-grotesk-regular text-white">
          <p>Lowkey will buzz in guests on your behalf so you don't have to answer buzzer calls.</p>
          <p>Use Lowkey to buzz in guests in 3 easy steps.</p>
        </div>
      </div>

      <div className="sm:flex sm:flex-row sm:justify-center sm:pt-5">
        <div className="flex max-w-7xl flex-col justify-center space-y-16 px-2 pt-16 sm:flex-row sm:space-x-8 sm:space-y-0 sm:px-0 sm:py-0 lg:space-x-12">
          {howItWorksDescriptions.map((description, index) => (
            <FeatureDescription
              key={index}
              customFontSize={'text-2xl lg:text-3xl'}
              title={description.title}
              description={description.description}
              secondaryDescription={description.secondaryDescription}
              imageSrc={description.imageSrc}
              imageAlt={description.imageAlt}
              imageStyle={description.imageStyle}
              image={
                description.app && <LandingPhone channel={channel} kind={description.app} className="h-80 w-auto" />
              }
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default HowItWorks;
